"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, MessageSquare, PhoneCall } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const highlights = [{
  id: 1,
  title: "Method Feasibility",
  description: "Sample evaluations and optical feasibility testing for your process."
}, {
  id: 2,
  title: "Regulatory Compliance",
  description: "Full qualification packages aligned with pharmacopeia and GMP standards."
}, {
  id: 3,
  title: "System Integration",
  description: "Turnkey hardware, probe placement, and software validation workflows."
}];
export default function DiscussApplication() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="discussapplicationcta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="relative overflow-hidden border border-border bg-gradient-to-br from-secondary/80 via-card to-background shadow-md p-8 sm:p-12 lg:p-16">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase">
                  <FlaskConical className="size-4" />
                  <span>Technical & Application Support</span>
                </div>

                <div className="space-y-4">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                    Discuss your analytical testing and PAT requirements
                  </h2>
                  <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
                    Connect directly with our application specialists across Ireland and the UK. We assess sample compatibility, instrument specifications, and compliance standards for your site.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  {highlights.map((item, i) => <div key={item.id} data-index={i} className="rounded-lg border border-border/70 bg-card/60 p-3.5 backdrop-blur-xs">
                      <div className="flex items-center gap-2 mb-1.5 text-foreground font-semibold text-sm">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>{item.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-normal font-sans">
                        {item.description}
                      </p>
                    </div>)}
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-4">
                  <Button asChild size="lg" className="h-12 px-6 rounded-md shadow-sm">
                    <Link to="/DiscussYourApplicationPage">
                      <span>Discuss Your Application</span>
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="h-12 px-6 rounded-md">
                    <Link to="/BookADemoPage">
                      <MessageSquare className="size-4 mr-1 text-muted-foreground" />
                      <span>Book a Demonstration</span>
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-6">
                <div className="rounded-xl border border-border bg-card p-6 shadow-xs">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg bg-primary/10 p-3 text-primary shrink-0">
                      <PhoneCall className="size-6" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-base font-semibold text-foreground font-default">
                        Need immediate assistance?
                      </h3>
                      <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                        Speak directly with our technical team to discuss instrumentation fit, feasibility studies, or on-site support.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-border flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Direct Inquiries
                    </span>
                    <Button asChild variant="ghost" size="sm" className="text-primary font-medium">
                      <Link to="/ContactPage">
                        <span>Contact Specialist</span>
                        <ArrowRight className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-muted/40 p-5">
                  <p className="text-xs text-muted-foreground font-sans leading-relaxed">
                    Supporting pharmaceutical manufacturing, biopharma development, chemical production, and academic research teams.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}